// The booking form's SUBMIT DECISION CHAIN, as a PURE function.
//
// Extracted from BookingForm.submit() on 2026-08-30 so the client-side booking validity rules —
// and, above all, the ORDER they run in — can be VERIFIED. They could not be before, and that is
// how item D (8faef7f) survived: a booking with no student that also collided was unsaveable,
// looping between the collision panel and the soft-confirm panel for ever.
//
// ⚠ WHY THE SEAM IS HERE AND NOT AT buildPayload(). The obvious extraction is buildPayload, with a
// gate asserting it carries `extra`. That gate PASSES ON THE BROKEN CODE: submit() returned at the
// soft gate before buildPayload was ever called, so the acknowledgement was discarded upstream of
// the payload. The defect was in the DECISION SEQUENCE, not in payload construction. A gate aimed
// at the wrong seam is worse than no gate, because it reads as coverage. So it is the decision
// chain that moved here, and buildPayload deliberately did NOT — it feeds create, edit,
// maintenance, unavailability, series and offline paths, and breaking it breaks all booking
// creation, which is far worse than the deadlock this protects against.
//
// No React, no vite-only imports — plain TS, so database/verify-booking-submit-gate.ts can import
// it. localToUtcIso comes from ops-board/theme.ts, which has no imports of its own; the capacity
// and external-aircraft types come from lib/opsBoardTypes.ts, which likewise has none.
//
// ─── THE TWO INVARIANTS THIS FILE ENCODES ────────────────────────────────────────────────────
//  1. ORDER IS LOAD-BEARING. First match wins. A booking missing both a base and a date must
//     report the BASE error. The date guard in particular exists to run before localToUtcIso,
//     which throws on an empty date string — it is not merely cosmetic and must not be reordered.
//  2. `extra` SURVIVES THE SOFT GATE. When the soft challenge interrupts a submit, the
//     acknowledgement that submit was carrying (save_as_waitlist, displace_pending,
//     displace_confirmed, displace_notify, ack_external_overlap) travels WITH the challenge and is
//     replayed intact. The collision ack and the no-student ack answer two independent questions
//     and can both be outstanding at once. Dropping either one re-collides on the server for ever.

import { localToUtcIso } from '../../components/ops-board/theme';
import type { ExternalAircraftSource, ParticipantCapacity } from '../opsBoardTypes';

// Non-instructor (student-line) capacities — a client occupies the same person line as a student.
// This list is what the soft challenge reads; BookingForm imports it from here.
export const STUDENT_LINE_CAPACITIES: ParticipantCapacity[] = ['student', 'client'];

// External (non-fleet) aircraft source labels. Lives here because the rule-4 message interpolates
// them; BookingForm imports it back for its chip rows so there is ONE source of the wording.
export const EXTERNAL_AC_LABELS: Record<ExternalAircraftSource, string> = {
  crosshire: 'Crosshire', client_supplied: 'Client supplied',
};

/** Default base timezone, matching the form's own `base?.timezone ?? 'Australia/Darwin'`. */
export const DEFAULT_TZ = 'Australia/Darwin';

/** One external aircraft row, as the form holds it (ExtAcRow). Untrimmed, as typed. */
export interface SubmitExternalAircraft {
  source: ExternalAircraftSource;
  type: string;
  registration: string;
}

/** One participant row, as the form holds it. Only `capacity` bears on the decision. */
export interface SubmitParticipant {
  capacity: ParticipantCapacity;
}

/**
 * Plain data lifted from BookingForm's component state. No React types, no Booking, no Base — the
 * caller reduces its state to this so the chain can be driven without a DOM.
 *
 * `repeat` is deliberately ABSENT: the recurring path calls submitSeries() and returns before any
 * of this, and that branch stays in submit() ahead of the call.
 */
export interface SubmitInput {
  base_id: string;
  /**
   * The selected booking kind (booking type). REQUIRED BY THE FORM from 0.12.5 (Ben, 2026-09-07),
   * and required HERE rather than in the database: `Task.booking_kind_id` stays nullable and live
   * kindless bookings exist, so a NOT NULL constraint would need a backfill and a ruling on what
   * those rows become. Empty string = nothing selected, matching the form's `<option value="">`.
   */
  booking_kind_id: string;
  date: string;
  startTime: string;
  entryMode: 'duration' | 'finish';
  finishDate: string;
  finishTime: string;
  wholeDay: boolean;
  lastDay: string;
  aircraftRegs: string[];
  assetIds: string[];
  externalAc: SubmitExternalAircraft[];
  isMaintenance: boolean;
  isUnavailability: boolean;
  editingSeriesId: string | null;
  participants: SubmitParticipant[];
  /** The selected base's timezone; undefined/null falls back to DEFAULT_TZ, as the form does. */
  timezone?: string | null;
}

export type SubmitDecision =
  | { kind: 'error'; message: string }
  | { kind: 'softConfirm'; message: string; extra: Record<string, unknown> }
  | { kind: 'proceed'; extra: Record<string, unknown> };

/** The exact soft-challenge wording. Preserved verbatim from submit(); rewording is a separate call. */
export const SOFT_NO_STUDENT_MESSAGE =
  'You haven\'t nominated a student or client for this booking. Proceed anyway?';

/**
 * Decide what submit() should do, without doing it.
 *
 * @param input   plain state lifted from the form
 * @param extra   the acknowledgement this submit is carrying (collision acks, waitlist, …)
 * @param ackSoft true when the user has already answered the soft challenge ("Proceed anyway")
 */
export function evaluateBookingSubmit(
  input: SubmitInput,
  extra: Record<string, unknown>,
  ackSoft: boolean,
): SubmitDecision {
  const {
    base_id, booking_kind_id, date, startTime, entryMode, finishDate, finishTime, wholeDay, lastDay,
    aircraftRegs, assetIds, externalAc, isMaintenance, isUnavailability, editingSeriesId,
    participants,
  } = input;

  // 1. Base required.
  if (!base_id) return { kind: 'error', message: 'Base is required.' };

  // 2. Date required. A copy seeds an empty date (the operator must choose the new one); this
  //    guards every localToUtcIso call below, which throws on an empty date. DO NOT REORDER.
  if (!date) return { kind: 'error', message: 'Pick a date for this booking.' };

  // 3. Booking kind required (Ben, 2026-09-07). ⚠ IT MUST SIT HERE, ABOVE THE RESOURCE RULES.
  //    Rules 4 and 6 branch on isMaintenance / isUnavailability, and BOTH are derived from the
  //    kind — with none selected they are both false, so a maintenance booking with no kind would
  //    fall past them to the crew floor and report "Add at least one person", which is not the
  //    problem the operator has. Naming the missing kind first is the only honest first match.
  if (!booking_kind_id) return { kind: 'error', message: 'Booking type is required.' };

  // 4. PR-000010: a resource is mandatory ONLY for maintenance / unavailability / series-occurrence
  //    bookings. Ordinary bookings may carry just people — see the floor at rules 7-8.
  const hasResource = aircraftRegs.length > 0 || assetIds.length > 0 || externalAc.length > 0;
  const resourceExemptKind = isMaintenance || isUnavailability || !!editingSeriesId;
  if (resourceExemptKind && !hasResource && !(isUnavailability && participants.length > 0)) {
    return { kind: 'error', message: 'Select an aircraft, an asset, and/or an external aircraft for this booking.' };
  }

  // 5. External aircraft need a type + registration (mirrors the server CHECK; clearer here).
  const badExt = externalAc.find((e) => !e.type.trim() || !e.registration.trim());
  if (badExt) {
    return { kind: 'error', message: `Each ${EXTERNAL_AC_LABELS[badExt.source].toLowerCase()} aircraft needs a type and a registration.` };
  }

  // 6. PR-000027: a maintenance booking type applies to FLEET aircraft only. Reject an external-only
  //    allocation set (>=1 external, 0 fleet) with the same message the server enforces — surfaced,
  //    not silently filtered, so the user learns why. Mixed (fleet + external) stays allowed.
  if (isMaintenance && externalAc.length > 0 && aircraftRegs.length === 0) {
    return {
      kind: 'error',
      message: 'Maintenance booking types apply to fleet aircraft only — external/cross-hire aircraft are maintained by their provider. Pick a different booking type or add the fleet aircraft.',
    };
  }

  // A recurring-series occurrence is exempt from the crew requirement (mirrors the backend §3.4
  // exemption) — "assign on the day" may add only an aircraft without crew.
  const isSeriesOccurrence = !!editingSeriesId;
  if (!isMaintenance && !isUnavailability && !isSeriesOccurrence) {
    // 7. PR-000010 floor (Ben 2026-06-23): need at least one person, and either a second person
    //    OR a resource. A lone person with no resource is not a valid booking.
    if (participants.length === 0) {
      return { kind: 'error', message: 'Add at least one person (instructor, student or client) to this booking.' };
    }
    // 8. One person needs a resource.
    if (participants.length === 1 && !hasResource) {
      return { kind: 'error', message: 'A booking needs at least two people, or one person plus a resource (aircraft, asset or room). Add another person or a resource.' };
    }
    // 9. PR-000010 soft challenge: confirm (don't block) when no student/client is nominated.
    //    THE ITEM D SEAM. The challenge carries `extra` back out with it, because this gate returns
    //    BEFORE buildPayload(extra) in the caller — without the carry, a collision ack is silently
    //    discarded and the server just collides again: an unbreakable loop for a booking with no
    //    student. `extra` is returned BY REFERENCE, exactly as the component stashed it.
    const hasStudentOrClient = participants.some((p) => STUDENT_LINE_CAPACITIES.includes(p.capacity));
    if (!hasStudentOrClient && !ackSoft) {
      return { kind: 'softConfirm', message: SOFT_NO_STUDENT_MESSAGE, extra };
    }
  }

  const tz = input.timezone ?? DEFAULT_TZ;
  const startIso = localToUtcIso(date, startTime, tz);
  if (wholeDay) {
    // 10. Whole-day: the only user-facing constraint is a valid last day on/after the start date
    //    (finish = lastDay + 1 at 00:00, so end > start is guaranteed once lastDay >= start date).
    if (!lastDay) return { kind: 'error', message: 'Pick the last day for this whole-day booking.' };
    if (lastDay < date) return { kind: 'error', message: 'The last day cannot be before the start date.' };
  } else if (entryMode === 'finish') {
    // 11. Finish mode: a finish date is required and finish must be after start.
    if (!finishDate) return { kind: 'error', message: 'Pick a finish date for this booking.' };
    const endIso = localToUtcIso(finishDate, finishTime, tz);
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      return { kind: 'error', message: 'Finish (date + time) must be after the start.' };
    }
  }

  return { kind: 'proceed', extra };
}
